/**
 * Card database loader.
 *
 * Loads every JSON file under src/data/cards/ and src/data/decks/ plus
 * tokens.json and factions.json, validates in dev, indexes everything, and
 * registers itself with the engine.
 *
 * This module must never throw on missing or partial data: the card set is
 * written incrementally, so an empty cards folder is a valid state.
 */

import type {
  CardDefinition,
  CardType,
  DeckDefinition,
  Faction,
  Keyword,
  Rarity,
  Subtype,
  TokenDefinition,
} from '@/engine/cardTypes';
import { registerCardDb } from '@/engine/cardDb';
import { expandDeck, validateDeck, type DeckValidation } from '@/engine/deckRules';
import rawTokens from './tokens.json';
import rawFactions from './factions.json';
import { asCard, asDeck, asToken, validateCardSet, type Issue, type SetValidation } from './validate';

export interface FactionInfo {
  id: Faction;
  name: string;
  shortName: string;
  symbol: string;
  glyph: string;
  color: string;
  colorDim: string;
  gasName: string;
  walletName: string;
  motto: string;
  identity: string;
  strengths: string[];
  weaknesses: string[];
  keywords: Keyword[];
  subtypes: Subtype[];
  allies: Faction[];
  rivals: Faction[];
  archetypes: string[];
}

// ---------------------------------------------------------------------------
// Raw file collection
// ---------------------------------------------------------------------------

type Glob = Record<string, unknown>;

function globJson(modules: Glob): { raw: unknown; where: string }[] {
  const out: { raw: unknown; where: string }[] = [];
  for (const [path, mod] of Object.entries(modules)) {
    const file = path.split('/').pop() ?? path;
    if (Array.isArray(mod)) {
      mod.forEach((entry, i) => out.push({ raw: entry, where: `${file}[${i}]` }));
    } else if (mod && typeof mod === 'object') {
      out.push({ raw: mod, where: file });
    }
  }
  return out;
}

const cardModules = import.meta.glob('./cards/*.json', { eager: true, import: 'default' }) as Glob;
const deckModules = import.meta.glob('./decks/*.json', { eager: true, import: 'default' }) as Glob;

const rawCardEntries = globJson(cardModules);
const rawDeckEntries = globJson(deckModules);
const rawTokenEntries = (rawTokens as unknown[]).map((raw, i) => ({ raw, where: `tokens.json[${i}]` }));

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export const validation: SetValidation = validateCardSet({
  cards: rawCardEntries,
  tokens: rawTokenEntries,
  decks: rawDeckEntries,
});

/** Every validation issue, for the Test Lab error panel. */
export const dataIssues: Issue[] = validation.issues;

const isDev = (() => {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
})();

if (isDev && validation.errors.length > 0) {
  // Never throw: the app must still boot so the Test Lab can show the list.
  console.error(
    `[gaswars] ${validation.errors.length} card data error(s). Run "npm run cards:validate" for details.`,
    validation.errors.slice(0, 20),
  );
}

// ---------------------------------------------------------------------------
// Indexes
// ---------------------------------------------------------------------------

function isUsable(entry: { raw: unknown }): boolean {
  return typeof entry.raw === 'object' && entry.raw !== null;
}

/**
 * Cards that at least have an id and a type are loaded even if they carry
 * validation errors, so the Test Lab can show a broken card next to its error.
 */
export const allCards: CardDefinition[] = rawCardEntries
  .filter(isUsable)
  .map((e) => asCard(e.raw))
  .filter((c) => typeof c.id === 'string' && c.id.length > 0)
  .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

export const allTokens: TokenDefinition[] = rawTokenEntries
  .filter(isUsable)
  .map((e) => asToken(e.raw))
  .filter((t) => typeof t.id === 'string' && t.id.length > 0);

export const allDecks: DeckDefinition[] = rawDeckEntries
  .filter(isUsable)
  .map((e) => asDeck(e.raw))
  .filter((d) => typeof d.id === 'string' && d.id.length > 0)
  .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

export const allFactions: FactionInfo[] = rawFactions as FactionInfo[];

const cardIndex = new Map<string, CardDefinition>();
for (const c of allCards) cardIndex.set(c.id, c);

const tokenIndex = new Map<string, TokenDefinition>();
for (const t of allTokens) tokenIndex.set(t.id, t);

const deckIndex = new Map<string, DeckDefinition>();
for (const d of allDecks) deckIndex.set(d.id, d);

const factionIndex = new Map<string, FactionInfo>();
for (const f of allFactions) factionIndex.set(f.id, f);

export const cardsByFaction: Record<string, CardDefinition[]> = {};
for (const c of allCards) {
  const key = c.faction ?? 'unknown';
  (cardsByFaction[key] ??= []).push(c);
}

export const cardsByType: Record<string, CardDefinition[]> = {};
for (const c of allCards) {
  const key = c.type ?? 'unknown';
  (cardsByType[key] ??= []).push(c);
}

const keywordIndex = new Map<string, CardDefinition[]>();
for (const c of allCards) {
  const printed = Array.isArray(c.keywords) ? c.keywords : [];
  const granted = c.upgrade?.keywords ?? [];
  for (const k of new Set([...printed, ...granted])) {
    const list = keywordIndex.get(k) ?? [];
    list.push(c);
    keywordIndex.set(k, list);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getCard(id: string): CardDefinition | undefined {
  return cardIndex.get(id);
}

export function getToken(id: string): TokenDefinition | undefined {
  return tokenIndex.get(id);
}

export function getDeck(id: string): DeckDefinition | undefined {
  return deckIndex.get(id);
}

export function getFaction(id: string): FactionInfo | undefined {
  return factionIndex.get(id);
}

export function byKeyword(keyword: Keyword): CardDefinition[] {
  return keywordIndex.get(keyword)?.slice() ?? [];
}

export interface SearchFilters {
  text?: string;
  faction?: Faction[];
  type?: CardType[];
  rarity?: Rarity[];
  keyword?: Keyword[];
  subtype?: Subtype[];
  minCost?: number;
  maxCost?: number;
  set?: string[];
  legendary?: boolean;
  /** Format name, e.g. "genesis". Cards banned in it are dropped. */
  legalIn?: string;
}

function totalCostOf(card: CardDefinition): number {
  const c = card.cost ?? {};
  return (
    (c.generic ?? 0) + (c.btc ?? 0) + (c.eth ?? 0) + (c.sol ?? 0) + (c.bnb ?? 0) + (c.xrp ?? 0)
  );
}

export function search(filters: SearchFilters = {}): CardDefinition[] {
  const needle = filters.text?.trim().toLowerCase();
  return allCards.filter((card) => {
    if (filters.faction && !filters.faction.includes(card.faction)) return false;
    if (filters.type && !filters.type.includes(card.type)) return false;
    if (filters.rarity && !filters.rarity.includes(card.rarity)) return false;
    if (filters.set && !filters.set.includes(card.set)) return false;
    if (filters.legendary !== undefined && (card.legendary === true) !== filters.legendary) return false;
    if (filters.keyword) {
      const have = [...(card.keywords ?? []), ...(card.upgrade?.keywords ?? [])];
      if (!filters.keyword.every((k) => have.includes(k))) return false;
    }
    if (filters.subtype) {
      const have = card.subtypes ?? [];
      if (!filters.subtype.some((s) => have.includes(s))) return false;
    }
    const cost = totalCostOf(card);
    if (filters.minCost !== undefined && cost < filters.minCost) return false;
    if (filters.maxCost !== undefined && cost > filters.maxCost) return false;
    if (filters.legalIn && card.legality?.[filters.legalIn] === 'banned') return false;
    if (needle) {
      const haystack = [card.name, card.text, card.flavor ?? '', ...(card.subtypes ?? []), ...(card.tags ?? [])]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

/** Expand a stored deck into the flat id list createGame wants. */
export function deckCardIds(deck: DeckDefinition): string[] {
  return expandDeck(deck.cards ?? []);
}

export function validateStoredDeck(deck: DeckDefinition): DeckValidation {
  return validateDeck(deckCardIds(deck), { getCard });
}

// ---------------------------------------------------------------------------
// Engine registration
// ---------------------------------------------------------------------------

registerCardDb({ getCard, getToken });

export const cardDb = { getCard, getToken };
